import React, { useState, useEffect } from "react";
import { Navbar } from "./Navbar";
import { FaBuilding, FaInfoCircle, FaFileInvoice, FaCheckCircle } from "react-icons/fa";
import { toast } from "react-toastify";

const STORAGE_KEY = "companyBillingInfo";
const ADDRESS_MAX_LENGTH = 110;

const defaultForm = {
  companyName: "",
  mobile: "",
  gstNumber: "",
  notGstRegistered: false,
  address: "",
  pincode: "",
  country: "India",
  addressTitle: "",
};

export const Company_GST = () => {
  const [form, setForm] = useState(defaultForm);
  const [savedInfo, setSavedInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        setSavedInfo(data);
        setForm({
          companyName: data.companyName || "",
          mobile: data.mobile || "",
          gstNumber: data.gstNumber || "",
          notGstRegistered: !!data.notGstRegistered,
          address: data.address || "",
          pincode: data.pincode || "",
          country: data.country || "India",
          addressTitle: data.addressTitle || "",
        });
      } else {
        setSavedInfo(null);
      }
    } catch (_) {
      setSavedInfo(null);
    }
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        gstNumber: form.notGstRegistered ? "" : (form.gstNumber || "").trim(),
        notGstRegistered: form.notGstRegistered,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setSavedInfo(payload);
      toast.success("Billing information saved. It will be used for GST invoices.");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 md:mb-0">
            Company <span className="text-red-500">/ GST</span>
          </h1>
          <Navbar />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left: Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 md:p-8">
              <h2 className="text-lg sm:text-xl font-semibold text-red-500 mb-5 md:mb-6 flex items-center gap-2">
                <FaFileInvoice className="text-red-500" />
                Billing information
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                Save your company and GST details to get GST invoices with your orders.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={form.companyName}
                      onChange={handleChange}
                      placeholder="Your company or business name"
                      className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Mobile</label>
                    <div className="flex">
                      <span className="inline-flex items-center border border-r-0 border-gray-300 rounded-l-lg px-3 py-2.5 bg-gray-50 text-sm text-gray-600">
                        +91
                      </span>
                      <input
                        type="tel"
                        name="mobile"
                        value={form.mobile}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        className="flex-1 border border-gray-300 rounded-r-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">GST number</label>
                  <input
                    type="text"
                    name="gstNumber"
                    value={form.gstNumber}
                    onChange={handleChange}
                    placeholder="e.g. 27ABCDE1234F1Z5"
                    disabled={form.notGstRegistered}
                    className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <label className="flex items-center gap-2 mt-2 text-gray-600 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      name="notGstRegistered"
                      checked={form.notGstRegistered}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300 text-sky-500 focus:ring-sky-500"
                    />
                    My company is not GST registered
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Billing address</label>
                  <textarea
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Street, area, city"
                    maxLength={ADDRESS_MAX_LENGTH}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-y min-h-[80px]"
                  />
                  <p className="text-xs text-gray-500 text-right mt-1">{form.address.length}/{ADDRESS_MAX_LENGTH}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={form.pincode}
                      onChange={handleChange}
                      placeholder="e.g. 110001"
                      maxLength={6}
                      className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Country</label>
                    <input
                      type="text"
                      value={form.country}
                      readOnly
                      className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 bg-gray-50 text-gray-600 text-sm sm:text-base"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address title (optional)</label>
                  <input
                    type="text"
                    name="addressTitle"
                    value={form.addressTitle}
                    onChange={handleChange}
                    placeholder="e.g. Office, Head office"
                    className="w-full border border-gray-300 rounded-lg px-3 sm:px-4 py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="bg-sky-500 hover:bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-lg text-sm sm:text-base transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving…" : "Save billing information"}
                </button>
              </form>
            </div>
          </div>

          {/* Right: Saved summary + Why GST */}
          <div className="space-y-5 lg:space-y-6">
            {/* Saved billing summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FaCheckCircle className="text-green-500" />
                Saved billing
              </h3>
              {savedInfo ? (
                <div className="text-sm text-gray-700 space-y-2">
                  {savedInfo.companyName && <p><span className="font-medium">Company:</span> {savedInfo.companyName}</p>}
                  {savedInfo.mobile && <p><span className="font-medium">Mobile:</span> +91 {savedInfo.mobile}</p>}
                  {savedInfo.gstNumber ? (
                    <p><span className="font-medium">GSTIN:</span> {savedInfo.gstNumber}</p>
                  ) : savedInfo.notGstRegistered ? (
                    <p className="text-amber-700">Not GST registered</p>
                  ) : null}
                  {savedInfo.address && <p><span className="font-medium">Address:</span> {savedInfo.address}</p>}
                  {(savedInfo.pincode || savedInfo.country) && (
                    <p>{[savedInfo.pincode, savedInfo.country].filter(Boolean).join(", ")}</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No billing info saved yet. Fill the form and save.</p>
              )}
            </div>

            {/* Why add GST card */}
            <div className="bg-sky-50 border border-sky-100 rounded-xl p-5 sm:p-6">
              <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <FaInfoCircle className="text-sky-500" />
                Why add GST details?
              </h3>
              <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>Get GST invoices for your business orders</li>
                <li>Claim input tax credit (ITC) on purchases</li>
                <li>Comply with business accounting requirements</li>
                <li>Use the same details across all orders</li>
              </ul>
            </div>

            {/* Quick tip */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 flex gap-3">
              <FaBuilding className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-800 mb-1">GSTIN format</p>
                <p>15 characters: 2 (state) + 10 (PAN) + 2 (entity) + 1 (Z) + 1 (check digit). Example: 27ABCDE1234F1Z5.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
